import { api } from '@organization/api'
import { MFAPurpose } from '@organization/models'
import { Text } from '@shared/components'
import { getDeviceName, showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  App,
  Button,
  Flex,
  Form,
  Input,
  ModalProps,
  Spin,
  Typography
} from 'antd'
import { AxiosError } from 'axios'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useStyle } from './AuthWrapper/styles'

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
}

type Props = {
  mfaAccessToken?: string
  expireOtpCode?: number
  onNextStep?: (token: string) => void
} & ModalProps

export const ConfirmChangePasswordOtpCode = ({
  mfaAccessToken,
  expireOtpCode = 0,
  onCancel,
  onNextStep
}: Props) => {
  const { styles } = useStyle()
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const { message } = App.useApp()
  const [remainingSeconds, setRemainingSeconds] = useState<number>(
    expireOtpCode ?? 0
  )
  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const hasExpiredRef = useRef<boolean>(false)

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }

    if (expireOtpCode !== undefined && expireOtpCode > 0) {
      hasExpiredRef.current = false
      setRemainingSeconds(expireOtpCode)

      timerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            hasExpiredRef.current = true
            if (timerRef.current) {
              clearInterval(timerRef.current)
              timerRef.current = null
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }
    }
  }, [expireOtpCode])

  useEffect(() => {
    if (remainingSeconds === 0 && hasExpiredRef.current) {
      form.setFields([
        { name: 'otp_code', errors: [t('error.otp_expired')], value: '' }
      ])
    }
  }, [remainingSeconds, form, t])

  const checkErrorCodes = (
    error: AxiosError<{ code: string; detail: string }>
  ) => {
    const code = error.response?.data?.code
    const detail = error.response?.data?.detail

    if (code === 'token_not_valid') {
      showApiErrors({ body: t('error.token_not_valid') })

      return
    } else if (code?.includes('otp') && detail) {
      showApiErrors({ response: { data: { otp_code: detail } } }, form)
      return
    }

    showApiErrors(error, form)
  }

  const { mutate: verifyOtp, isPending: isVerifyOtpPending } = useMutation({
    mutationFn: api.mfa.verifyOtp,
    onSuccess: (data) => {
      if (data?.change_password_access_token) {
        onNextStep?.(data?.change_password_access_token)
      }
    },
    onError: checkErrorCodes
  })

  const { mutate: resendOtp, isPending: isResendOtpPending } = useMutation({
    mutationFn: api.mfa.sendOtp,
    onSuccess: (data) => {
      message.success(t('otp_code_sent_successfully'))

      const expiresIn = Number(data?.expires_in) || 0
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = null
      }

      hasExpiredRef.current = false
      setRemainingSeconds(expiresIn)

      if (expiresIn > 0) {
        timerRef.current = setInterval(() => {
          setRemainingSeconds((prev) => {
            if (prev <= 1) {
              hasExpiredRef.current = true
              if (timerRef.current) {
                clearInterval(timerRef.current)
                timerRef.current = null
              }
              return 0
            }
            return prev - 1
          })
        }, 1000)
      }
    },
    onError: checkErrorCodes
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    const deviceName = getDeviceName()

    verifyOtp({
      ...values,
      device_name: deviceName,
      __mfaAccessToken: mfaAccessToken
    })
  }

  return (
    <Form
      form={form}
      layout="vertical"
      className={styles.form}
      requiredMark={false}
    >
      <Flex justify="center">
        <Form.Item
          name="otp_code"
          label={t('form.item.code_otp')}
          rules={[{ required: true, message: t('form.message.code_otp') }]}
        >
          <Input.OTP
            disabled={remainingSeconds < 1}
            className="w-full"
            size="large"
            formatter={(str) => str.replace(/\D/g, '')}
          />
        </Form.Item>
      </Flex>

      {remainingSeconds > 0 && (
        <Flex justify="end">
          <Text color="colorPrimary">
            {t('common.code_expired_in', {
              time: formatTime(remainingSeconds)
            })}
          </Text>
        </Flex>
      )}

      <Flex vertical gap={16}>
        <Flex justify="end">
          <Spin size="small" spinning={isResendOtpPending}>
            <Typography.Link
              disabled={remainingSeconds > 0}
              onClick={() =>
                resendOtp({
                  __mfaAccessToken: mfaAccessToken || '',
                  purpose: MFAPurpose.CHANGE_PASSWORD
                })
              }
            >
              {t('resend_otp_code')}
            </Typography.Link>
          </Spin>
        </Flex>

        <Flex align="baseline" justify="center" gap={16}>
          <Button type="default" onClick={onCancel}>
            {t('button.cancel')}
          </Button>

          <Button
            type="primary"
            htmlType="submit"
            loading={isVerifyOtpPending}
            disabled={remainingSeconds < 1}
            onClick={onSubmit}
          >
            {t('button.confirm')}
          </Button>
        </Flex>
      </Flex>
    </Form>
  )
}
